import { ReactNode } from "react";
import { Citation, TaskLabels } from "../shared/types";
import { z } from "zod";

export type ChatEventStatus = "idle" | "pending" | "streaming" | "complete" | "error" | "cancelled";

export type ChatEvent =
  | { type: "session_created"; sessionId: string }
  | { type: "status_change"; status: ChatEventStatus; sessionId: string };

export interface AgentAction {
  id?: string;
  type: "info" | "analysis";
  content: string;
  timestamp: number;
  artifacts?: Artifact[];
}

export enum LoadingState {
  LOADING = "LOADING",
  LOADED = "LOADED",
  ERROR = "ERROR",
  UNINITIALIZED = "UNINITIALIZED",
  CANCELLED = "cancelled",
}

export interface AssistantMeta {
  avatar: string;
  name: string;
}

export interface Feedback {
  rating?: "up" | "down";
  comment?: string;
}

export type ContextOption =
  | {
      label: string;
      key: string;
      type: "submenu";
      options: { id: string; label: string }[];
      icon?: ReactNode;
      /** When set, selecting an option triggers this instead of toggling it
       *  into the session context (e.g. slash-command insertion). */
      onOptionSelect?: (option: { id: string; label: string }) => void;
    }
  | {
      label: string;
      key: string;
      type: "trigger";
      handler?: () => void;
      icon?: ReactNode;
    };

export interface ChatSession {
  id: string;
  messages: ChatMessage[];
  context?: {
    files?: File[];
    [x: string]: unknown;
  };
  selectedModel?: string | null;
  datamates?: string[];
  status?: SessionStatusEnum;
}

export interface FileUploadProps {
  allowedFiles: string; // e.g. ".txt,.json,.md"
  contextFieldKey: string;
  enforceAllowedFiles?: boolean;
}

export interface ChatbotUrls {
  origin?: string;
  askPath?: string;
  proceedPath?: string;
  frontendUrl?: string;
  followupPath?: string;
  entityDetectionUrl?: string;
  feedbackPath?: string;
}

export interface Datamate {
  id: string;
  label: string;
}

export enum EntityType {
  SNOWFLAKE_ACCOUNT = "snowflake_account",
  SNOWFLAKE_WAREHOUSE = "snowflake_warehouse",
  SNOWFLAKE_DATABASE = "snowflake_database",
  SNOWFLAKE_TABLE = "snowflake_table",
  SNOWFLAKE_USER = "snowflake_user",
  SNOWFLAKE_ROLE = "snowflake_role",
  WAREHOUSE = "warehouse",
  TABLE = "table",
  QUERY = "query",
  USER = "user",
  WORKLOAD = "workload",
  // Databricks-only entity types (AI-6339). The shared values above
  // (warehouse/query) double as Databricks types when paired with a
  // databricks provider context — the FE picks the URL builder per provider.
  CLUSTER = "cluster",
  JOB = "job",
  // Tableau citation entity types. The backend `TableauCitationResolver`
  // rewrites the model-emitted LUID / email into a URL-encoded full
  // `tableau_*:N://site.luid` rk before the FE sees it, so the entityId
  // passed to the URL builder is already encoded for path use.
  TABLEAU_DASHBOARD = "tableau_dashboard",
  TABLEAU_USER = "tableau_user",
  TABLEAU_DATASOURCE = "tableau_datasource",
  TABLEAU_DASHBOARD_GROUP = "tableau_dashboard_group",
  TABLEAU_JOB = "tableau_job",
  UNKNOWN = "unknown",
}

export interface DetectedEntity {
  name: string;
  types_found: EntityType[];
  selected_type: EntityType;
}

export interface EntityDetectionRequest {
  text: string;
  detected_entities?: string[];
}

export interface EntityDetectionResponse {
  entities: Pick<DetectedEntity, "name" | "types_found">[];
  original_text: string;
}

export interface ChatbotProps {
  helloMessage?: ReactNode;
  assistantMeta?: AssistantMeta;
  context?: Record<string, unknown>;
  fileUploadProps?: FileUploadProps;
  contextOptions?: ContextOption[];
  /** Per-command helper text shown under the input (see QuestionForm). */
  commandHints?: Record<string, string>;
  placeholder?: string;
  autoFocus?: boolean;
  classNames?: {
    chatbot?: string;
    chatMessages?: string;
    messageItem?: string;
    questionForm?: string;
    chatbotScrollingContent?: string;
  };
  initialMessage?: string;
  submitInitialMessageOnMount?: boolean;
  questionFormLeftActions?: ReactNode[];
  refetchSession?: () => Promise<unknown>;
  chatboxStartPosition?: "middle" | "bottom";
  helloMessageFooter?: ReactNode;
  handleSavePrompt?: (message: ChatMessage) => void;
  showSavePromptButton?: boolean;
}

export interface ChatbotProviderProps extends ChatbotProps {
  taskLabel?: keyof typeof TaskLabels;
  initialValidation?: ChatState["initialValidation"];
  onError?: (error: unknown) => void;
  onEvent?: (event: ChatEvent) => void;
  urls?: ChatbotUrls;
  /**
   * AI-6339: tenant provider context. Drives per-provider citation URL routing
   * inside the markdown renderer. Defaults to `"snowflake"` when omitted.
   */
  provider?: "snowflake" | "databricks";
  disableContext?: boolean;
  requestParams?: RequestInit;
  components?: {
    feedback?: ReactNode;
    questionFormButtons?: (
      userPrompt: string,
      onAccept: (improvedPrompt: string) => void
    ) => ReactNode;
    messageActions?: (ctx: {
      sessionId?: string;
      messageId: string;
      isLastMessage: boolean;
    }) => ReactNode;
    // Opt-in REPLACEMENT for the default ShareMessage button (which opens
    // EmailDialog). When provided, every per-message Share button in this
    // chatbot surface renders the consumer's component instead. Studio
    // uses this to swap in StudioShareButton, which opens the full
    // ShareDialog (Link / Slack / Email tabs) — see AI-6343.
    shareButton?: (ctx: { sessionId?: string }) => ReactNode;
    // AI-7498: inline editable form rendered under an assistant message that
    // carries an `opportunityDraft`. Studio supplies OpportunityDraftForm,
    // which prefills from the draft and publishes on Submit.
    opportunityDraftForm?: (ctx: {
      draft: OpportunityDraftFinding;
      sessionId?: string;
    }) => ReactNode;
  };
  contextOptions?: ContextOption[];
  /** Per-command helper text shown under the input (see QuestionForm). */
  commandHints?: Record<string, string>;
  isMarkdownResponse?: boolean;
  models?: { label: string; value: string }[];
  placeholder?: string;
  sessions?: Record<string, ChatSession>;
  currentSessionId?: string;
  selectedDatamateId?: string;
  handleSavePrompt?: (message: ChatMessage) => void;
}

export type InteractionType = "text" | "select" | "multiSelect" | "confirm";

export interface InteractionChoice {
  id: string;
  label: string;
  value: string;
}

export interface InteractionRequest {
  type: InteractionType;
  id: string;
  prompt: string;
  required?: boolean;
  choices?: InteractionChoice[];
  validation?: {
    pattern?: string;
    message?: string;
  };
}

export interface UploadedFile {
  filename: string;
  content: string; // base64 encoded content
  content_type: string;
  size: number;
}

export interface MessageAttachment {
  id: string;
  attachment_name: string;
  file_type: string | null;
  s3_url: string | null;
  extracted_content: string | null;
  order: number;
  attachment_type: string; // 'file', 'datamate', 'knowledge_base'
  attachment_reference_id: string | null;
  file_size: number | null;
  created_on: string; // ISO date string
}

/**
 * AI-7498: the agent-drafted opportunity finding streamed on the
 * `opportunity_draft` channel. The producer fills every field to its best
 * inferred value; the FE renders it as an editable form and publishes on
 * Submit. Field vocabulary mirrors the backend `OpportunityFinding` schema.
 */
export interface OpportunityDraftResource {
  resource_name: string;
  resource_type: string;
  resource_uri: string;
  resource_id: string;
  verified: boolean;
}

// Every field is optional: the payload rides the stream opaquely and a draft
// may be partial — the HOST (apps/web `parseFinding`) normalizes it into the
// full shape before the form renders. Keeping the type honest here removes the
// need for unchecked casts to a fully-populated interface.
export interface OpportunityDraftFinding {
  title?: string;
  description?: string;
  resource_name?: string;
  estimated_savings_usd_per_month?: number;
  effort?: string;
  resources?: OpportunityDraftResource[];
  /** Source analysis turn, or "" for a cold direct-add draft. */
  turn_uuid?: string;
  /** Stamped by a successful submit; a re-fetched session then renders the
   *  Published card and Edit updates the same opportunity. */
  published?: { opportunity_id: string; instance_id: number } | null;
  /** Stamped by `POST /studio/opportunities/discard`; a re-fetched session
   * renders the collapsed Rejected card instead of a live editable draft. */
  discarded?: { at?: string | null; by?: string | null } | null;
}

/**
 * A clarifying question the agent asked with Claude's
 * `AskUserQuestion` tool. The `ask_user_question` stream event carries this
 * payload as a JSON string in `content`; the turn renders it as a question card
 * and the answer is sent as the user's next message.
 */
export const askUserQuestionPayloadSchema = z.object({
  tool_use_id: z.string().default(""),
  questions: z
    .array(
      z.object({
        question: z.string().min(1),
        header: z.string().default(""),
        multiSelect: z.boolean().default(false),
        options: z
          .array(z.object({ label: z.string().min(1), description: z.string().default("") }))
          .default([]),
      })
    )
    .min(1),
});
export type AskUserQuestionPayload = z.infer<typeof askUserQuestionPayloadSchema>;

export interface ChatMessage {
  id: string;
  content: string;
  role: "user" | "assistant";
  timestamp: number;
  actions?: AgentAction[];
  interaction?: InteractionRequest;
  artifacts?: Artifact[];
  heading: string;
  citations?: Citation[];
  todos?: TodoItem[];
  attachments?: MessageAttachment[];
  confidence_str?: string;
  /** AI-7498: agent-drafted opportunity, rendered as an inline editable form. */
  opportunityDraft?: OpportunityDraftFinding;
  /** The agent's clarifying question, rendered as a question card. */
  askUserQuestion?: AskUserQuestionPayload;
}

export interface Artifact {
  content: string;
  entity: string;
  name: string;
  type: string;
}

export interface ChatResponse {
  content: string;
  actions?: AgentAction[];
  interaction?: InteractionRequest;
  role: "assistant" | "user";
  timestamp: number;
  artifacts?: Artifact[];
  heading: string;
  todos?: TodoItem[];
  opportunityDraft?: OpportunityDraftFinding;
  askUserQuestion?: AskUserQuestionPayload;
}

// Session Status Enum
export const sessionStatusSchema = z.enum(["failed", "completed", "pending", "cancelled"]);
export type SessionStatusEnum = z.infer<typeof sessionStatusSchema>;

/**
 * Contextual chat error (AI-7467). `code` is a resolved Studio taxonomy
 * `error_code`; `retryable` gates the Retry affordance in the UI.
 */
export interface ChatError {
  message: string;
  code?: string;
  retryable?: boolean;
}

export interface ChatState {
  sessions: Record<string, ChatSession>;
  currentSessionId: string | null;
  loadingState: LoadingState;
  error: ChatError | null;
  abortController: AbortController | null;
  currentActions: AgentAction[];
  currentTodos: TodoItem[];
  taskLabel?: keyof typeof TaskLabels;
  initialValidation?: (data: { context: ChatSession["context"] }) => Promise<void>;
  urls?: ChatbotUrls;
  /**
   * AI-6339: provider context plumbed through useChatContext() to the
   * markdown renderer for per-provider citation URL routing.
   */
  provider?: "snowflake" | "databricks";
  disableContext?: boolean;
  requestParams?: RequestInit;
  isMarkdownResponse?: boolean;
  models?: { label: string; value: string }[];
  classNames?: {
    chatbot?: string;
    chatMessages?: string;
    messageItem?: string;
    questionForm?: string;
    chatbotScrollingContent?: string;
  };
  components?: {
    feedback?: ReactNode;
    questionFormButtons?: (
      userPrompt: string,
      onAccept: (improvedPrompt: string) => void
    ) => ReactNode;
    messageActions?: (ctx: {
      sessionId?: string;
      messageId: string;
      isLastMessage: boolean;
    }) => ReactNode;
    shareButton?: (ctx: { sessionId?: string }) => ReactNode;
    opportunityDraftForm?: (ctx: {
      draft: OpportunityDraftFinding;
      sessionId?: string;
    }) => ReactNode;
  };
}

export const todoItemSchema = z.object({
  id: z.string(),
  content: z.string(),
  status: z.enum(["pending", "in_progress", "completed"]),
  priority: z.enum(["high", "medium", "low"]),
});

export type TodoItem = z.infer<typeof todoItemSchema>;

export interface ToolUsageData {
  tool: string;
  id: string;
}

export interface ProgressUpdate {
  timestamp: string;
  todos: TodoItem[];
}

export interface FinalResponseData {
  tool_usage?: ToolUsageData[];
  turns?: number;
  tools_used?: number;
  progress_updates?: ProgressUpdate[];
  total_progress_updates?: number;
}

export const agentStreamResponseSchema = z.object({
  id: z.coerce.string().optional(),
  type: z.enum([
    "info",
    "agent_outcome",
    "require_user_action",
    "analysis",
    "error",
    "citations",
    "text",
    "tool_use",
    "final_response",
    "complete",
    "stream_complete", // Backend uses this as end marker
    "opportunity_draft", // AI-7498: agent-drafted opportunity finding
    "ask_user_question", // the agent's clarifying question (JSON content)
    "custom",
  ]),
  heading: z.string().optional(),
  content: z.string().optional(),
  tool: z.string().optional(),
  todos: z.array(todoItemSchema).optional(),
  response: z.string().optional(),
  data: z
    .object({
      tool_usage: z
        .array(
          z.object({
            tool: z.string(),
            id: z.string(),
          })
        )
        .optional(),
    })
    .optional(),
  interaction: z
    .object({
      type: z.enum(["text", "multiSelect", "confirm", "select"]),
      id: z.string(),
      prompt: z.string(),
      required: z.boolean().optional(),
      choices: z
        .array(
          z.object({
            id: z.string(),
            label: z.string(),
            value: z.string(),
          })
        )
        .optional(),
    })
    .optional(),
  artifacts: z
    .array(
      z.object({
        content: z.string(),
        entity: z.string(),
        name: z.string(),
        type: z.string(),
      })
    )
    .optional(),
  citations: z
    .array(
      z.object({
        content: z.string(),
        entity: z.string(),
        name: z.string(),
        type: z.string(),
      })
    )
    .optional(),
  // AI-7498: raw finding payload on the `opportunity_draft` channel. Passed
  // through to the FE form, which parses it with its own schema + defaults.
  finding: z.record(z.unknown()).optional(),
  confidence_str: z.string().optional(),
  // Studio error taxonomy (AI-7467). Present on `type: "error"` events.
  error_code: z.string().optional(),
  error_type: z.string().optional(),
  retryable: z.boolean().optional(),
});

export interface AgentStreamResponse {
  id?: string;
  type:
    | "info"
    | "agent_outcome"
    | "require_user_action"
    | "analysis"
    | "error"
    | "citations"
    | "text"
    | "tool_use"
    | "final_response"
    | "complete"
    | "stream_complete" // Backend uses this as end marker
    | "opportunity_draft" // AI-7498: agent-drafted opportunity finding
    | "ask_user_question"; // the agent's clarifying question (JSON content)
  heading?: string;
  content?: string;
  tool?: string;
  todos?: TodoItem[];
  response?: string;
  data?: FinalResponseData;
  interaction?: {
    type: "text" | "multiSelect" | "confirm" | "select";
    id: string;
    prompt: string;
    required?: boolean;
    choices?: Array<{
      id: string;
      label: string;
      value: string;
    }>;
  };
  artifacts?: Artifact[];
  citations?: Citation[];
  confidence_str?: string;
  finding?: Record<string, unknown>;
  // Studio error taxonomy (AI-7467). Present on `type: "error"` events.
  error_code?: string;
  error_type?: string;
  retryable?: boolean;
}
